import React, { useEffect, useRef, useState } from 'react';
import { Button, Input, Modal } from 'antd';
import type { TextAreaRef } from 'antd/es/input/TextArea';
import css from './CommentPopover.module.scss';

// Backend contract: notes are 2..500 characters.
const MIN_NOTE_LENGTH = 2;
const MAX_NOTE_LENGTH = 500;

interface CommentPopoverProps {
  open: boolean;
  /** Human label of the clicked section (e.g. "Summary", "Acme — bullet 2"). */
  sectionLabel: string;
  onSubmit: (note: string) => void;
  onClose: () => void;
  /** True while the comment placeholder is being created. */
  busy: boolean;
  /** Remove the clicked target — spawns a delete card the user then Accepts. */
  onRemove: () => void;
  /** Precise destructive label for the clicked target (e.g. "Remove this
   *  bullet", "Remove the Experience section"). */
  removeLabel: string;
  /** True while the remove request is in flight. */
  removeBusy?: boolean;
  /** Structural route out of the comment box: a form for the clicked entry
   *  ("Edit details" on a job or school) or a new one ("Add a job" / "Add a
   *  school" on a section heading). Supplied together or not at all — without
   *  a handler the click has nowhere to go, so the button is not rendered, and
   *  the page withholds both wherever there is no entry behind the target.
   *
   *  The LABEL is the caller's to own, and “Edit details” in particular is a
   *  cross-repo contract: beast-api's structural-guidance copy tells the user
   *  to look for a button by that exact name. */
  entryActionLabel?: string;
  onEntryAction?: () => void;
}

/**
 * Comment-on-document entry point: opened by the editor page when the user
 * clicks a section in the preview. One antd Modal for every viewport —
 * centered on desktop, near-fullscreen on phones via antd's own responsive
 * behavior — deliberately the simplest thing that works on both.
 */
const CommentPopover: React.FC<CommentPopoverProps> = ({
  open,
  sectionLabel,
  onSubmit,
  onClose,
  busy,
  onRemove,
  removeLabel,
  removeBusy = false,
  entryActionLabel,
  onEntryAction,
}) => {
  const [note, setNote] = useState('');
  const noteInputRef = useRef<TextAreaRef>(null);

  // Each open is a fresh conversation about a (possibly different) section —
  // never leak the previous note into it.
  useEffect(() => {
    if (open) setNote('');
  }, [open]);

  // Either request in flight freezes the whole modal (Esc/mask/other buttons).
  const anyBusy = busy || removeBusy;
  const trimmed = note.trim();
  const canSubmit = !anyBusy && trimmed.length >= MIN_NOTE_LENGTH;
  const submit = () => {
    if (canSubmit) onSubmit(trimmed);
  };

  return (
    <Modal
      title={`What should change in “${sectionLabel}”?`}
      open={open}
      onCancel={onClose}
      // While a request round-trips, the modal must not silently vanish —
      // Esc/mask are gated to match the disabled Cancel button.
      keyboard={!anyBusy}
      maskClosable={!anyBusy}
      // antd's focus trap grabs the dialog wrapper on open, so a bare
      // autoFocus is unreliable (especially on reopen) — focus the textarea
      // once the open motion settles instead.
      afterOpenChange={(opened) => {
        if (opened) noteInputRef.current?.focus();
      }}
      width="min(520px, 94vw)"
      className={css.commentModal}
      footer={
        <div className={css.footer}>
          {onEntryAction && entryActionLabel && (
            <Button
              className={css.entryAction}
              disabled={anyBusy}
              onClick={onEntryAction}
            >
              {entryActionLabel}
            </Button>
          )}
          <Button
            danger
            className={css.removeAction}
            loading={removeBusy}
            disabled={busy}
            onClick={onRemove}
          >
            {removeLabel}
          </Button>
          <Button onClick={onClose} disabled={anyBusy}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={busy}
            disabled={!canSubmit}
            onClick={submit}
          >
            Send
          </Button>
        </div>
      }
    >
      <p className={css.explainer}>
        Tell us what's off — we'll draft a stronger version for you to review.
      </p>
      <Input.TextArea
        ref={noteInputRef}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        onKeyDown={(e) => {
          // Cmd/Ctrl+Enter = Send, same guards as the button (2-char + busy).
          if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            submit();
          }
        }}
        placeholder="Tell us what to improve here — e.g. 'too wordy' or 'lead with the leadership part'"
        autoSize={{ minRows: 3, maxRows: 6 }}
        maxLength={MAX_NOTE_LENGTH}
        showCount
        disabled={busy}
        dir="auto"
      />
    </Modal>
  );
};

export default CommentPopover;
